import { Injectable } from "@angular/core";
import { poe1 } from "../../../game";
import { itemTypesWithTwoSockets } from "../../data/poe2/item-lists";
import { Comparator, Rarity, WeaponType, ArmourType, BaseTypeTier } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { Filter, CustomRule, AdvancedConditions } from "../../models/filter-models";
import { RuleGeneratorService } from "./rule-generator.service";
import { ItemDataService } from "../item-data.service";
import { GenerationHelperService } from "./generation-helper.service";
import { CraftingBasesGeneratorService } from "./crafting-bases-generator.service";

@Injectable({
  providedIn: 'root'
})
export class Poe2CraftingBasesGeneratorService {
  constructor(
    private ruleFormatter: RuleGeneratorService,
    private itemDataService: ItemDataService,
    private helper: GenerationHelperService,
    private craftingBasesGenerator: CraftingBasesGeneratorService,
  ) {
  }

  public generateILevelCraftingBaseGearHighlightsRules(filter: Filter): string {
    if (poe1) return '';

    let iLevel82NormalExceptions = '';
    let iLevel81NormalExceptions = '';

    if (filter.showILevel82NormalGear) {
      iLevel82NormalExceptions = this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearILevel82,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          advancedConditions: new AdvancedConditions({ minItemLevel: 82 }),
          baseTypes: this.helper.buildBaseTypesForTier(filter.showILevel82NormalGearMinBaseTypeTier, [WeaponType.All], undefined),
        }),
        filter,
        'Item Level 82 Weapon Crafting Bases',
        { customClass: this.helper.buildWeaponClass([WeaponType.All]) }
      );

      iLevel82NormalExceptions += '\n\n' + this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearILevel82,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          advancedConditions: new AdvancedConditions({ minItemLevel: 82 }),
          baseTypes: this.helper.buildBaseTypesForTier(filter.showILevel82NormalGearMinBaseTypeTier, undefined, [ArmourType.All]),
        }),
        filter,
        'Item Level 82 Armour Crafting Bases',
        { customClass: this.helper.buildArmourClass([ArmourType.All]) }
      );
    }

    if (filter.showILevel81NormalGear) {
      iLevel81NormalExceptions = this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearILevel81,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          advancedConditions: new AdvancedConditions({ minItemLevel: 81 }),
          baseTypes: this.helper.buildBaseTypesForTier(filter.showILevel81NormalGearMinBaseTypeTier, [WeaponType.All], undefined),
        }),
        filter,
        'Item Level 81 Weapon Crafting Bases',
        { customClass: this.helper.buildWeaponClass([WeaponType.All]) }
      );

      iLevel81NormalExceptions += '\n\n' + this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearILevel81,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          advancedConditions: new AdvancedConditions({ minItemLevel: 81 }),
          baseTypes: this.helper.buildBaseTypesForTier(filter.showILevel81NormalGearMinBaseTypeTier, undefined, [ArmourType.All]),
        }),
        filter,
        'Item Level 81 Armour Crafting Bases',
        { customClass: this.helper.buildArmourClass([ArmourType.All]) }
      );
    }

    return iLevel82NormalExceptions + '\n\n' + iLevel81NormalExceptions;
  }

  public generateExceptionalGearHighlightsRules(filter: Filter): string {
    if (poe1) return '';

    let excellentSocketedExceptions = '';
    let goodSocketedExceptions = '';
    let excellentQualityExceptions = '';
    let goodQualityExceptions = '';

    if (filter.showExcellentExceptionalSocketedGear || filter.showGoodExceptionalSocketedGear) {
      const weaponTypesWithTwoSockets = this.itemDataService.getAllWeaponTypes().filter(wt => itemTypesWithTwoSockets.includes(wt) && wt != WeaponType.Quivers) as WeaponType[];
      const weaponTypesWithOneSocket = this.itemDataService.getAllWeaponTypes().filter(wt => !itemTypesWithTwoSockets.includes(wt) && wt != WeaponType.Quivers) as WeaponType[];
      const armourTypesWithTwoSockets = this.itemDataService.getAllArmourTypes().filter(at => itemTypesWithTwoSockets.includes(at)) as ArmourType[];
      const armourTypesWithOneSocket = this.itemDataService.getAllArmourTypes().filter(at => !itemTypesWithTwoSockets.includes(at)) as ArmourType[];

      if (filter.showExcellentExceptionalSocketedGear) {
        excellentSocketedExceptions = this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearExcellentExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 3 }),
            baseTypes: this.helper.buildBaseTypesForTier(filter.showExcellentExceptionalSocketedGearMinBaseTypeTier, weaponTypesWithTwoSockets, undefined),
          }),
          filter,
          'Excellent Exceptional 2-Socketed Weapons',
          { customClass: this.helper.buildWeaponClass(weaponTypesWithTwoSockets) }
        ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearExcellentExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 3 }),
            baseTypes: this.helper.buildBaseTypesForTier(filter.showExcellentExceptionalSocketedGearMinBaseTypeTier, undefined, armourTypesWithTwoSockets),
          }),
          filter,
          'Excellent Exceptional 2-Socketed Armour',
          { customClass: this.helper.buildArmourClass(armourTypesWithTwoSockets) }
        ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearExcellentExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 2 }),
            baseTypes: this.helper.buildBaseTypesForTier(filter.showExcellentExceptionalSocketedGearMinBaseTypeTier, weaponTypesWithOneSocket, undefined),
          }),
          filter,
          'Excellent Exceptional 1-Socketed Weapons',
          { customClass: this.helper.buildWeaponClass(weaponTypesWithOneSocket) }
        ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearExcellentExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 2 }),
            baseTypes: this.helper.buildBaseTypesForTier(filter.showExcellentExceptionalSocketedGearMinBaseTypeTier, undefined, armourTypesWithOneSocket),
          }),
          filter,
          'Excellent Exceptional 1-Socketed Armour',
          { customClass: this.helper.buildArmourClass(armourTypesWithOneSocket) }
        );
      }

      if (filter.showGoodExceptionalSocketedGear) {
        goodSocketedExceptions = this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearGoodExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 3 }),
            baseTypes: this.helper.buildBaseTypesForTier(filter.showGoodExceptionalSocketedGearMinBaseTypeTier, weaponTypesWithTwoSockets, undefined),
          }),
          filter,
          'Good Exceptional 2-Socketed Weapons',
          { customClass: this.helper.buildWeaponClass(weaponTypesWithTwoSockets) }
        ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearGoodExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 3 }),
            baseTypes: this.helper.buildBaseTypesForTier(filter.showGoodExceptionalSocketedGearMinBaseTypeTier, undefined, armourTypesWithTwoSockets),
          }),
          filter,
          'Good Exceptional 2-Socketed Armour',
          { customClass: this.helper.buildArmourClass(armourTypesWithTwoSockets) }
        ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearGoodExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 2 }),
            baseTypes: this.helper.buildBaseTypesForTier(filter.showGoodExceptionalSocketedGearMinBaseTypeTier, weaponTypesWithOneSocket, undefined),
          }),
          filter,
          'Good Exceptional 1-Socketed Weapons',
          { customClass: this.helper.buildWeaponClass(weaponTypesWithOneSocket) }
        ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearGoodExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 2 }),
            baseTypes: this.helper.buildBaseTypesForTier(filter.showGoodExceptionalSocketedGearMinBaseTypeTier, undefined, armourTypesWithOneSocket),
          }),
          filter,
          'Good Exceptional 1-Socketed Armour',
          { customClass: this.helper.buildArmourClass(armourTypesWithOneSocket) }
        ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
          EquipmentType.OtherGearGoodExceptionalSocketed,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            advancedConditions: new AdvancedConditions({ minSockets: 2 }),
          }),
          filter,
          'Good Exceptional Socketed Small Gear',
          { customClass: '"Boots" "Gloves" "Foci"' }
        );
      }
    }

    if (filter.showExcellentExceptionalQualityGear) {
      excellentQualityExceptions = this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearExcellentExceptionalQuality,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          advancedConditions: new AdvancedConditions({ minQuality: filter.showExcellentExceptionalQualityGearMinQuality ?? 28 }),
          baseTypes: this.helper.buildBaseTypesForTier(filter.showExcellentExceptionalQualityGearMinBaseTypeTier, [WeaponType.All], undefined),
        }),
        filter,
        'Excellent Exceptional Quality Weapons',
        { customClass: this.helper.buildWeaponClass([WeaponType.All]) }
      ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearExcellentExceptionalQuality,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          advancedConditions: new AdvancedConditions({ minQuality: filter.showExcellentExceptionalQualityGearMinQuality ?? 28 }),
          baseTypes: this.helper.buildBaseTypesForTier(filter.showExcellentExceptionalQualityGearMinBaseTypeTier, undefined, [ArmourType.All]),
        }),
        filter,
        'Excellent Exceptional Quality Armour',
        { customClass: this.helper.buildArmourClass([ArmourType.All]) }
      );
    }

    if (filter.showGoodExceptionalQualityGear) {
      goodQualityExceptions = this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearGoodExceptionalQuality,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          advancedConditions: new AdvancedConditions({ minQuality: filter.showGoodExceptionalQualityGearMinQuality ?? 21 }),
          baseTypes: this.helper.buildBaseTypesForTier(filter.showGoodExceptionalQualityGearMinBaseTypeTier, [WeaponType.All], undefined),
        }),
        filter,
        'Good Exceptional Quality Weapons',
        { customClass: this.helper.buildWeaponClass([WeaponType.All]) }
      ) + '\n\n' + this.ruleFormatter.generateEquipmentRule(
        EquipmentType.OtherGearGoodExceptionalQuality,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          advancedConditions: new AdvancedConditions({ minQuality: filter.showGoodExceptionalQualityGearMinQuality ?? 21 }),
          baseTypes: this.helper.buildBaseTypesForTier(filter.showGoodExceptionalQualityGearMinBaseTypeTier, undefined, [ArmourType.All]),
        }),
        filter,
        'Good Exceptional Quality Armour',
        { customClass: this.helper.buildArmourClass([ArmourType.All]) }
      );
    }

    return excellentQualityExceptions + '\n\n' + goodQualityExceptions + '\n\n'
      + excellentSocketedExceptions + '\n\n' + goodSocketedExceptions + '\n\n'
      + this.craftingBasesGenerator.generateExcellentAndGoodJewelleryBaseTypesRules(filter);
  }

  public generateSpecialGearHighlightRules(filter: Filter): string {
    if (poe1 || !filter.showSpecialGear) return '';

    const baseTypes = this.itemDataService.getSpecialGearBaseTypes();
    if (baseTypes.length === 0) return '';

    return this.ruleFormatter.generateEquipmentRule(
      EquipmentType.OtherGearSpecialGear,
      new CustomRule({ baseTypes }),
      filter,
      'Special Gear',
    );
  }

}
